// @ts-nocheck

import { filter, find, forEach, includes } from 'lodash';
import MutationObserver from 'mutation-observer';
import type { ElementRef, Node } from 'react';
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import v4 from 'uuid';

import WinamaxLogo from '../../assets/img/mobile/logoW_with_padding.png';
import { HighlightsSvg, Podium, Ranking, WinaTVSvg } from '../../assets/svg';
import { FaceToFaceSvg } from '../../assets/svg/index';
import { ReactComponent as Pitch } from '../../assets/svg/pitch.svg';
import { ReactComponent as Track } from '../../assets/svg/track.svg';
import { Target } from '../../client';
import { EventCenterNodeContext } from '../../contexts/EventCenterNodeContext';
import { usePrevious, useScript } from '../../hooks';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import useRouter from '../../hooks/useRouter';
import { getLang } from '../../i18n/l10n';
import { LiveVideoPlayer } from '../../modules/immersive/components/video-player';
import LineUpIcon from '../../modules/line-up/components/common/LineUpIcon';
import { SportsWithLineUpNewTag } from '../../modules/line-up/constants/runtime';
import { type UserJwtState } from '../../states/user';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import { NewTag } from '../../styled-components/shared';
import { type Highlight } from '../../types/match';
import { MatchViewModes } from '../../types/matchView';
import { type MatchViewMode } from '../../types/matchView';
import PeriodIds, { type PeriodId } from '../../types/periodIds';
import { SportIds } from '../../types/sports';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import Subject from '../../utils/subject';
import { checkIsLogged } from '../../utils/user';
import EventCenterNodeContainer from '../EventCenterNode/EventCenterNodeContainer';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel';
import MatchToolbarHighlights from '../MatchToolbarHighlights';
import MyMatchDynamicBackground, {
  MyMatchDynamicBackgroundLeftImgClassName,
  MyMatchDynamicBackgroundRadialClassName,
  MyMatchDynamicBackgroundRightImgClassName,
} from '../MyMatchDynamicBackground';
import PiPMatchToolbarController from '../PiPMatchToolbarController';
import { withStickyPlaceholder } from '../StickyWithPlaceholder';

import { ActiveWidgetBackground, SportradarInner, SportradarWrapper, Toolbar } from './MatchToolbar.style';

const URL = 'https://widgets.sir.sportradar.com/c1d88151bb6140ccf96759f133dba02f/widgetloader';
const EVENT_CENTER_WIDGET_BUTTON_ID = 'event-center-widget-button';

const Wrapper = styled.div``;

const StyledTvIcon = styled(WinaTVSvg)`
  height: 1.2em;
  width: 2em;
  padding-top: 2px;
`;

const StyledHighlightsIcon = styled(HighlightsSvg)`
  height: 1.2em;
  width: 2em;
`;

const StyledRanking = styled(Ranking)`
  height: 1.2em;
  width: 2em;
`;

const StyledPodium = styled(Podium)`
  height: 1.2em;
  width: 2em;
`;

const StyledFaceToFaceSvg = styled(FaceToFaceSvg)`
  height: 1.2em;
  width: 2em;
`;

const ContentWrapper = styled.div``;

const NoToolbar = styled.div`
  height: 0.05em;
`;

const customButtonsSharedStyle = css`
  position: relative;
  z-index: ${ZIndices.matchBetFilters - 1};
`;

const LineUpButtonWrapper = styled.div`
  ${customButtonsSharedStyle}
`;

const EventCenterButtonWrapper = styled.div`
  ${customButtonsSharedStyle}
`;

const StyledLineUpIcon = styled(LineUpIcon)`
  height: 1.2em;
  width: 2em;
`;

const StyledNewTag = styled(NewTag)`
  position: absolute;
  top: -1.2em;
  left: 50%;
  transform: translateX(-50%);
  font-size: 70%;
  margin: 0;
`;

const StyledMyMatchDynamicBackground = styled(MyMatchDynamicBackground)`
  --MyMatchDynamicBackground-defaultBackgroundColor: var(
    --MatchToolbar-dynamicDefaultBackgroundColor,
    ${theme.MatchBetGroup.backgroundColor}
  );

  & .${MyMatchDynamicBackgroundLeftImgClassName} {
    height: 500%;
    top: -8em;
    left: -3em;
  }

  & .${MyMatchDynamicBackgroundRightImgClassName} {
    top: 10%;
    right: -7em;
  }

  & .${MyMatchDynamicBackgroundRadialClassName} {
    top: -11em;
  }
`;

type LayoutType = 'single' | 'double' | 'topdown';
type MomentumType = 'disable' | 'bars' | 'line' | 'timeline' | 'compact';
type ScoreboardType = 'disable' | 'extended' | 'compact';
type CollapseToType = 'disable' | 'scoreboard' | 'momentum' | 'pitch';
type TabsPositionType = 'top' | 'bottom' | 'disable';
type DetailedScoreboardType = 'all' | 'momentum' | 'scoreDetails' | 'disable';
type ActiveSwitcherType = 'momentum' | 'scoreDetails';
type TabType = 'statistics' | 'headToHead' | 'standings' | 'timeline' | 'lineups';
type CommandKeyType = 'season.cupRoster' | 'season.liveTable';

type WidgetOptionsType = Partial<{
  seasonId: number | null;
  utid: number | null;
  uniqueTournamentId: number | null;
  disabledWidgetHeader: boolean;
}>;

export type MatchToolbarWidgetOptionsType = Partial<{
  disableWidgetHeader?: boolean;
  disablePitch?: boolean;
  layout?: LayoutType;
  disableComponents?: boolean;
  momentum?: MomentumType;
  scoreboard?: ScoreboardType;
  collapseTo?: CollapseToType;
  expanded?: boolean;
  tabsPosition?: TabsPositionType;
  detailedScoreboard?: DetailedScoreboardType;
  activeSwitcher?: ActiveSwitcherType;
  ballSpottingVisibleLines?: number;
  tabs?: { [tabId: number]: Array<TabType> };
  goalBannerImage?: string;
  matchId?: number;
  forceTeamInvert?: boolean;
  stageId?: number | null;
  sportId?: number;
}>;

export type BrWidgetPropsType = {
  brWidgetProps: {
    commandKey: CommandKeyType;
    widgetOptions: WidgetOptionsType;
  };
};

const defaultOptions: MatchToolbarWidgetOptionsType = {
  goalBannerImage: WinamaxLogo,
};

type TrackerOptionType = {
  options?: MatchToolbarWidgetOptionsType;
};

const sportradarDomSelectors = {
  pitch: '[d^="M0 5v21.84h6.85v"],[d^="M 0 5 v 21.84 h 6.85 v"]',
  headToHead: '[d^="M18 11.386V16h-1.126v-4.614a.36.36"],[d^="M 18 11.386V16h-1.126v-4.614a.36.36"]',
  standings: '[d^="M18 8.417v1.166h-5.625v4.084H6"],[d^="M 18 8.417v1.166h-5.625v4.084H6"]',
  statistics: '[d^="M4.636 9.912H1.727A.728.728"],[d^="M 4.636 9.912H1.727A.728.728"]',
  timeline: '[d^="M16 7.724v1.609c-.089"],[d^="M 16 7.724v1.609c-.089"]',
  lineups: '[d^="M17.928 4.463a.605.605"],[d^="M 17.928 4.463a.605.605 "]',
  track: '[d^="M10.16 2H5.48a2.84"],[d^="M 10.16 2 H 5.48 a 2.84"]',
  leaderboard: '[d^="M30 6.978A1 1 0 0029 6h"],[d^="M 30 6.978 A 1 1 0 0029 6 h"]',
  pointByPoint: '[d^="M15.367 2.636A8.975 8.97"],[d^="M 15.367 2.636A8.975 8.97"]',
  boxscore: '[d^="M17.357 0H5.786a.634.634"],[d^="M 17.357 0H5.786a.634.634"]',
};

const Widgets = {
  boxscore: 'boxscore',
  pitch: 'pitch',
  track: 'track',
};

export type WidgetWarningType = {
  widgetId: string;
  message: string;
};

const DEFAULT_POSITION_BOX_SCORE_WIDGET = 2;

type SportRadarProps = {
  hide?: boolean;
};

const Sportradar = forwardRef(function Sportradar({ hide = false }: SportRadarProps, ref) {
  const [dirty, setDirty] = useState(true);

  const styles = useMemo(() => {
    if (dirty || hide) {
      return {
        position: 'absolute',
        top: -9999999,
        height: 'auto',
        // $FlowFixMe
        width: Target.__MOBILE__ ? 320 : 500,
        opacity: 0,
      };
    } else {
      return {
        position: 'relative',
        top: 'auto',
        opacity: 1,
      };
    }
  }, [dirty, hide]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDirty(false);
    }, 500);
    return () => {
      clearTimeout(id);
    };
  }, []);

  return <SportradarInner style={styles} ref={ref} />;
});

const WidgetWarningInfosLabel = styled(InfosLabel)`
  margin: 0.5em 0.5em 0;
`;

type ContentProps = {
  isStreamPaused: boolean;
  matchId: number;
  shouldUsePlaceholder: boolean;
  isStreaming: boolean;
  sportradarHidden: boolean;
  isTvItemSelected: boolean;
  isHighlightsItemSelected: boolean;
  hasHighlights: boolean;
  highlights: Array<Highlight>;
  activeItem?: string;
  setActiveItem: (item: unknown) => void;
  tvItemId: string;
  trackerDomNodeRef: ElementRef<typeof Sportradar>;
  isSticky?: boolean;
  offsetTop?: number;
  placeholderRef: ElementRef<Node>;
  disableSticky?: boolean;
  setContentSize?: (size?: number) => void;
  streamData?: string;
  jwt: { token: string };
  sportId: number;
  categoryId: number;
  widgetWarningMessage?: string;
};

const Content = withStickyPlaceholder(
  ({
    isStreamPaused,
    matchId,
    shouldUsePlaceholder,
    isStreaming,
    sportradarHidden,
    isTvItemSelected,
    isHighlightsItemSelected,
    hasHighlights,
    highlights,
    activeItem,
    setActiveItem,
    tvItemId,
    trackerDomNodeRef,
    // props from withSticky
    isSticky,
    offsetTop,
    placeholderRef,
    setContentSize,
    streamData,
    jwt,
    sportId,
    categoryId,
    widgetWarningMessage,
  }: ContentProps) => {
    const measuredContentSize = (node) => {
      if (setContentSize) {
        setContentSize(node?.getBoundingClientRect()?.height);
      }
    };

    return (
      <ContentWrapper ref={measuredContentSize}>
        {shouldUsePlaceholder ? (
          <PiPMatchToolbarController
            matchId={matchId}
            isExpand={isStreaming}
            setActiveItem={setActiveItem}
            isTvItemSelected={isTvItemSelected}
            tvItemId={tvItemId}
            isSticky={isSticky}
            offsetTop={offsetTop}
            placeholderRef={placeholderRef}
          />
        ) : null}
        <SportradarWrapper>
          {/* it needs to be always rendered ! */}
          <Sportradar hide={sportradarHidden} ref={trackerDomNodeRef} />
        </SportradarWrapper>
        {isStreaming && !shouldUsePlaceholder ? (
          <LiveVideoPlayer matchId={matchId} pause={isStreamPaused} jwt={jwt} streamData={streamData} />
        ) : null}
        {isHighlightsItemSelected && hasHighlights ? (
          <MatchToolbarHighlights jwt={jwt} matchId={matchId} highlights={highlights} isSticky={isSticky} />
        ) : null}
        {activeItem === EVENT_CENTER_WIDGET_BUTTON_ID ? (
          <EventCenterNodeContainer sportId={sportId} categoryId={categoryId} matchId={matchId} />
        ) : null}
        {widgetWarningMessage ? (
          <WidgetWarningInfosLabel label={widgetWarningMessage} type={InfosLabelTypes.INFOS_RED} symbol="!" />
        ) : null}
      </ContentWrapper>
    );
  },
);

type MatchToolbarProps = {
  matchId: number;
  sportId: number;
  categoryId: number;
  hasTracker?: boolean;
  hasStream?: boolean;
  hasHighlights?: boolean;
  highlights?: Array<Highlight>;
  isStreamPaused?: boolean;
  isStreamHighlightsEnabled?: boolean;
  stickyContent?: boolean;
  connected?: boolean;
  options: MatchToolbarOptions;
  alreadyOpen?: boolean;
  className?: string;
  defaultTrackerTab: string;
  trackerName: string;
  showWidget?: boolean;
  jwt: UserJwtState | null;
  streamData?: string;
  displayLiveTable?: boolean;
  displayCupRoster?: boolean;
  liveTableProps?: BrWidgetPropsType | null;
  cupRosterProps?: BrWidgetPropsType | null;
  isLineUpAvailable?: boolean;
  isConnecting?: boolean;
  matchViewMode: MatchViewMode;
  isPrivateScore?: boolean;
  isMatchSportLineUpCompatible?: boolean;
  periodId?: PeriodId;
  isLive?: boolean;
  isEventCenterWidgetAvailable?: boolean;
  hiddenWidgetIds: Array<string>;
  widgetWarnings?: Array<WidgetWarningType>;
};

const MatchToolbar = ({
  matchId,
  sportId,
  categoryId,
  isStreamPaused = false,
  isStreamHighlightsEnabled = false,
  stickyContent = false,
  connected = false,
  isConnecting = false,
  hasStream = false,
  hasHighlights = false,
  highlights = [],
  hasTracker = false,
  options,
  defaultTrackerTab,
  trackerName,
  alreadyOpen = false,
  showWidget = false,
  className = '',
  jwt,
  streamData,
  displayLiveTable = false,
  displayCupRoster = false,
  liveTableProps = {},
  cupRosterProps = {},
  isLineUpAvailable = false,
  matchViewMode = MatchViewModes.CLASSIC,
  isPrivateScore,
  isMatchSportLineUpCompatible = false,
  periodId,
  isLive = false,
  isEventCenterWidgetAvailable = false,
  hiddenWidgetIds,
  widgetWarnings = [],
}: MatchToolbarProps): Node => {
  const { history, location } = useRouter();
  const { setWidgetSize, setIsWidgetSticky, setToolbarSize } = useMatchViewContext();

  const previousPeriodId = usePrevious(periodId);

  const tvItemIdRef = useRef<string>(v4());
  const highlightsItemIdRef = useRef<string>(v4());
  const trackerDomNodeRef = useRef<ElementRef<'div'> | null>(null);
  const toolbarDomNodeRef = useRef<ElementRef<'div'> | null>(null);
  const cupRosterIdRef = useRef<string>(v4());
  const liveTableIdRef = useRef<string>(v4());
  const optionsRef = useRef<TrackerOptionType>({ ...defaultOptions, ...options });
  const defaultValue = alreadyOpen ? defaultTrackerTab : ''; // for FORMULA 1
  const [activeItem, setActiveItem] = useState<string>(defaultValue);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [activeItemBgPosition, setActiveItemBgPosition] = useState(null);
  const [widgetIds, setWidgetIds] = useState([]);
  const isWidgetOpen = useMemo(() => activeItem !== '', [activeItem]);
  const isTvItemSelected = activeItem === tvItemIdRef.current;
  const isHighlightsItemSelected = activeItem === highlightsItemIdRef.current;
  const sportradarAvailable = isReady && hasTracker;
  const sportradarHidden = !hasTracker || !activeItem || isTvItemSelected || isHighlightsItemSelected;
  const isStreaming = isTvItemSelected && hasStream;
  const shouldUsePlaceholder = stickyContent;
  const {
    closeStream,
    floatingVideoRef,
    isPipCustomActive,
    isPipNativeActive,
    isSameMatchRouteAndPipNativeActive,
    setAskWtv,
    setJwt,
    setStreamData,
    isMatchRouteRelativeToStream,
  } = usePictureInPicture();

  const searchParams = new URLSearchParams(location.search);
  const highlightId = searchParams.get('highlightId');

  const isWtvOrDefaultTrackerActive =
    [tvItemIdRef.current, highlightsItemIdRef.current, defaultTrackerTab].indexOf(activeItem) !== -1;

  // we disable the sticky mode only for those two conditions:
  // 1 - when we don't click on the WTV or default tracker AND the pip is not active
  // 2 - when we don't click on the WTV or default tracker AND the pip is active AND at least one item is selected
  const disableSticky =
    !isWtvOrDefaultTrackerActive && (!isPipCustomActive || (isPipCustomActive && activeItem !== ''));

  // set language before loading script
  if (!window.SIR) {
    window.SIR = { o: { language: getLang() } };
  }

  // Attach SIR method to window object when loaded
  // https://store.sportradar.com/en/documentation/widgets/sir-api.php
  const [loaded, error] = useScript(URL);

  useEffect(() => {
    optionsRef.current = { ...defaultOptions, ...options };
  }, [options]);

  useEffect(() => {
    if (loaded && !error && !isReady) {
      const interval = setInterval(() => {
        if (typeof window.SIR === 'function') {
          setIsReady(true);
        }
      }, 50);
      return () => clearInterval(interval);
    }
  }, [loaded, error, isReady]);

  const onClickTvItem = useCallback(() => {
    if (!checkIsLogged(connected, history)) {
      return;
    }

    setActiveItem((prevState) => {
      if (prevState === tvItemIdRef.current) {
        // if we are not on the same match route and native pip is not active, we toggle off the item
        if (!isSameMatchRouteAndPipNativeActive) {
          return '';
        }

        // if we are on the same match route and native pip is activated, we let the toggle on
        return tvItemIdRef.current;
      }

      // if the WTV item is not selected, we select it by adding a red border bottom;
      return tvItemIdRef.current;
    });

    // if the WTV item is not selected, we ask for the WTV video by updating the askWtv state
    setAskWtv(() => {
      return activeItem !== tvItemIdRef.current;
    });

    if (isSameMatchRouteAndPipNativeActive && floatingVideoRef.current) {
      floatingVideoRef.current.style.removeProperty('z-index');
    }

    setStreamData(streamData);
    setJwt(jwt);
  }, [
    connected,
    history,
    setAskWtv,
    isSameMatchRouteAndPipNativeActive,
    floatingVideoRef,
    setStreamData,
    streamData,
    setJwt,
    jwt,
    activeItem,
  ]);
  const shouldCloseStreamPlayer = useMemo(
    () => !isMatchRouteRelativeToStream && (!isPipCustomActive || !isPipNativeActive),
    [isMatchRouteRelativeToStream, isPipCustomActive, isPipNativeActive],
  );

  useEffect(() => {
    if (shouldCloseStreamPlayer) {
      setActiveItem('');
      setAskWtv(false);
    }
  }, [shouldCloseStreamPlayer, setAskWtv]);

  const measuredToolbarHeight = useCallback(
    (node) => {
      if (node && isReady) {
        const { height } = node.getBoundingClientRect();
        if (setToolbarSize) {
          setToolbarSize(height);
        }
      }
    },
    [isReady, setToolbarSize],
  );

  const onClickHighlights = useCallback(() => {
    if (!isConnecting && !checkIsLogged(connected, history)) {
      return;
    }

    if (highlightId) {
      return setActiveItem(highlightsItemIdRef.current);
    }

    setActiveItem((prevState) => {
      if (prevState === highlightsItemIdRef.current) {
        // if we are not on the same match route, we toggle off the item
        return '';
      }

      return highlightsItemIdRef.current;
    });
  }, [highlightsItemIdRef, connected, isConnecting, history, highlightId]);

  useEffect(() => {
    if (highlightId) {
      onClickHighlights();
    }
  }, [highlightId, onClickHighlights]);

  const onClickCupRoster = useCallback(() => {
    history.push(
      RoutesRedirectionFactory.getCupRosterRouteConfig({
        state: {
          ...cupRosterProps,
        },
      }),
    );
  }, [cupRosterProps, history]);

  const onClickLiveTable = useCallback(() => {
    history.push(
      RoutesRedirectionFactory.getCupRosterRouteConfig({
        state: {
          ...liveTableProps,
        },
      }),
    );
  }, [liveTableProps, history]);

  const triggerWidgetClick = (id) => {
    const currentTrackerDomNodeRef = trackerDomNodeRef.current;
    if (currentTrackerDomNodeRef && id) {
      const originalItem = find(currentTrackerDomNodeRef.querySelectorAll('.sr-tabs-tab__wrapper'), (item) => {
        const widgetDataAttribute = item.getAttribute('data-test-tab');

        // for pitch widget, we need to target the svg whose parent has directly `srm-tab-1` className.
        // We can't query directly a specific svg `d` property because each sports has a different one
        if (id === Widgets.pitch) {
          return /pitch/i.test(widgetDataAttribute);
        }

        return widgetDataAttribute === id;
      });

      if (originalItem) {
        originalItem.click();
      }
    }
  };

  const onClickSportradarItem = useCallback(
    ({ currentTarget }) => {
      if (isSameMatchRouteAndPipNativeActive && floatingVideoRef.current) {
        floatingVideoRef.current.style.zIndex = '-1';
      }

      triggerWidgetClick(currentTarget?.id);
      setActiveItem((prevState) => (prevState === currentTarget.id ? '' : currentTarget.id));
    },
    [floatingVideoRef, isSameMatchRouteAndPipNativeActive],
  );

  useEffect(() => {
    if (periodId === PeriodIds.PERIOD_ID_PSO_START && periodId !== previousPeriodId) {
      if (isSameMatchRouteAndPipNativeActive && floatingVideoRef.current) {
        floatingVideoRef.current.style.zIndex = '-1';
      }

      triggerWidgetClick(Widgets.pitch);
      setActiveItem(Widgets.pitch);
    }
  }, [periodId, previousPeriodId, isSameMatchRouteAndPipNativeActive, floatingVideoRef]);

  const onLineUpButtonClick = useCallback(() => {
    history.push(RoutesRedirectionFactory.getLineUpRouteConfig(matchId));
  }, [history, matchId]);

  // insert a widget on a given position based on a target element
  const insertWidgetToPosition = (widget, position, targetElement) => {
    if (widget && targetElement) {
      targetElement.insertBefore(widget, targetElement.children[position]);
    }
  };

  useEffect(
    () => {
      const currentTrackerDomNodeRef = trackerDomNodeRef.current;
      const currentToolbarDomNodeRef = toolbarDomNodeRef.current;
      const isWidgetAlreadySelected = activeItem !== '' && activeItem !== tvItemIdRef.current;
      const initialWidgets = [
        Widgets.track,
        Widgets.pitch,
        'line-up-button',
        tvItemIdRef.current,
        liveTableIdRef.current,
        cupRosterIdRef.current,
        highlightsItemIdRef.current,
        EVENT_CENTER_WIDGET_BUTTON_ID,
      ];

      if (showWidget && isReady && currentTrackerDomNodeRef && currentToolbarDomNodeRef) {
        const clonedItems = {};
        const mutationWidgetIds = [];
        const matchToolbarWidgets = currentToolbarDomNodeRef.querySelectorAll('.sr-tabs-tab__wrapper');

        setWidgetIds([]);

        const observer = new MutationObserver((mutations) => {
          forEach(mutations, (mutation) => {
            forEach(mutation.addedNodes, (addedNode) => {
              if (includes(addedNode.classList, 'sr-lmt-plus-tabs__wrapper')) {
                // on item added
                const clonedItem = addedNode.cloneNode(true);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { pitch, track, ...selectors } = sportradarDomSelectors;
                forEach(selectors, (_, id) => {
                  if (!hiddenWidgetIds.find((widgetId) => widgetId === id)) {
                    if (
                      (!isMatchSportLineUpCompatible || (isMatchSportLineUpCompatible && id !== 'lineups')) &&
                      !(~['timeline', 'pointByPoint'].indexOf(id) && isPrivateScore)
                    ) {
                      const widgetItem = clonedItem.querySelector(`[data-test-tab="${id}"]`);
                      if (widgetItem) {
                        const widgetId = document.getElementById(id);

                        // remove the widgets which are already been on the DOM to avoid duplications due to mutation observer
                        if (widgetId && currentToolbarDomNodeRef.contains(widgetId)) {
                          currentToolbarDomNodeRef.removeChild(widgetId);
                        }

                        const children = widgetItem.children;
                        const divWidgetItem = document.createElement('div');
                        divWidgetItem.setAttribute('id', id);
                        divWidgetItem.className = `${widgetItem.className} widget__${id}`;
                        divWidgetItem.onclick = onClickSportradarItem;
                        divWidgetItem.append(...children);

                        if (id !== Widgets.boxscore) {
                          currentToolbarDomNodeRef.appendChild(divWidgetItem);
                        } else {
                          insertWidgetToPosition(
                            divWidgetItem,
                            DEFAULT_POSITION_BOX_SCORE_WIDGET,
                            currentToolbarDomNodeRef,
                          );
                        }

                        clonedItems[id] = divWidgetItem;
                        mutationWidgetIds.push(id);
                        setWidgetIds((prev) => [...prev, id]);
                      }
                    }
                  }
                });

                // ONLY when a widget is already selected (the WTV is excluded)
                if (isWidgetAlreadySelected) {
                  // when the match id changes by using the search bar,
                  // ... if a widget is already active, we trigger a click to update the old match widget to the new one
                  triggerWidgetClick(activeItem);
                }

                // ONLY when the native pip is activate
                // remove of floating video zIndex when the match id changes dynamically by using the search bar
                // ...otherwise the black placeholder on the bottom right stays hidden
                if (floatingVideoRef.current && isPipNativeActive) {
                  floatingVideoRef.current.style.removeProperty('z-index');
                }
              }
            });

            forEach(mutation.removedNodes, (removedNode) => {
              if (includes(removedNode.classList, 'sr-lmt-plus-tabs__wrapper')) {
                // on item removed
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { pitch, track, ...selectors } = sportradarDomSelectors;
                forEach(selectors, (selector, id) => {
                  if (!isMatchSportLineUpCompatible || (isMatchSportLineUpCompatible && id !== 'lineups')) {
                    if (removedNode.querySelector(selector) && clonedItems[id]) {
                      currentToolbarDomNodeRef.removeChild(clonedItems[id]);
                      delete clonedItems[id];
                      setWidgetIds((prev) => prev.filter((e) => e !== id));
                    }
                  }
                });
              }
            });
          });

          // array of unused widgets which will be removed from the toolbar
          const unusedMatchWidgets = filter(
            matchToolbarWidgets,
            ({ id }) => !mutationWidgetIds.includes(id) && !initialWidgets.includes(id),
          );

          // remove all the unnecessaries widgets when we change dynamically the match id by using the search bar
          if (unusedMatchWidgets.length) {
            forEach(unusedMatchWidgets, (widget) => {
              if (widget.parentNode) {
                widget.parentNode.removeChild(widget);
              }
            });
          }
        });

        observer.observe(currentTrackerDomNodeRef, { childList: true, subtree: true });
        window.SIR('addWidget', currentTrackerDomNodeRef, trackerName, optionsRef.current);

        return () => {
          observer.disconnect();
          window.SIR('removeWidget', currentTrackerDomNodeRef);
        };
      }
    },
    // we don't need to add all the dependencies to avoid unecessary rerendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      floatingVideoRef,
      isReady,
      onClickSportradarItem,
      showWidget,
      trackerName,
      matchId,
      sportId,
      isPrivateScore,
      hiddenWidgetIds,
    ],
  );

  useEffect(
    () => {
      if (isPipCustomActive || isPipNativeActive) {
        return;
      }

      // ONLY when whe WTV is previously selected and we have a live stream with the new match id
      // updates the live video stream when the match id changes dynamically and we still have a live
      if (isStreaming) {
        setAskWtv(true);
      }

      // ONLY when the WTV is previously selected and we don't have a live stream
      // close the live stream when the match id changes dynamically and we don't have a live anymore
      if (isTvItemSelected && !hasStream) {
        closeStream();
      }
    },
    // we don't need to add all the dependencies to avoid unecessary rerendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchId],
  );

  useEffect(() => {
    const activeElt = document.getElementById(activeItem);

    if (activeElt) {
      const { offsetWidth: width, offsetHeight: height, offsetTop: top, offsetLeft: left } = activeElt;
      setActiveItemBgPosition({ width, height, top, left });
    } else {
      setActiveItemBgPosition(null);
    }
  }, [activeItem, hasStream, sportradarAvailable, widgetIds, highlightsItemIdRef]);

  useEffect(() => {
    if (setIsWidgetSticky) {
      setIsWidgetSticky(!disableSticky);
    }
  }, [setIsWidgetSticky, disableSticky]);

  const hasLineUpNewTag = useMemo(() => SportsWithLineUpNewTag.indexOf(Number(sportId)) !== -1, [sportId]);

  // Display order :
  // [ Tv | Pitch/Track (sportradar) | Head2head | Standings | Timeline | Lineups ]

  const onEventCenterButtonClick = useCallback((e) => {
    const { currentTarget } = e;

    setActiveItem((prevState) => (prevState === currentTarget.id ? '' : currentTarget.id));
  }, []);

  useLayoutEffect(() => {
    // Open IMG widget on render if eligible
    if (isEventCenterWidgetAvailable) {
      setTimeout(() => {
        setActiveItem(EVENT_CENTER_WIDGET_BUTTON_ID);
      }, 0);
    }
  }, [isEventCenterWidgetAvailable]);

  const eventCenterNodeRoutingChannel = useMemo(() => new Subject(), []);
  const [eventCenterNodeLaunchView, setEventCenterNodeLaunchView] = useState(null);

  const onGolfWinaTvButtonClick = useCallback(() => {
    setActiveItem(EVENT_CENTER_WIDGET_BUTTON_ID);
    const launchView = 'course';

    eventCenterNodeRoutingChannel.next({
      view: launchView,
    });

    setEventCenterNodeLaunchView(launchView);
  }, [eventCenterNodeRoutingChannel]);

  const eventCenterNodeForwardedState = useMemo(
    () => ({
      routingChannel: eventCenterNodeRoutingChannel,
      launchView: eventCenterNodeLaunchView,
      setLaunchView: setEventCenterNodeLaunchView,
    }),
    [eventCenterNodeRoutingChannel, eventCenterNodeLaunchView],
  );

  const EventCenterButtonIcon = useMemo(() => {
    if (sportId === SportIds.GOLF) {
      return <StyledPodium />;
    }

    if (sportId === SportIds.MMA) {
      return <StyledFaceToFaceSvg />;
    }
  }, [sportId]);

  const currentWidgetWarning = useMemo(
    () => widgetWarnings.find((widgetWarning) => widgetWarning.widgetId === activeItem),
    [widgetWarnings, activeItem],
  );

  return (
    <Wrapper ref={measuredToolbarHeight}>
      {isEventCenterWidgetAvailable ||
      (isReady && (hasStream || sportradarAvailable || displayLiveTable || displayCupRoster || isLineUpAvailable)) ? (
        <>
          <Toolbar ref={toolbarDomNodeRef} className={className} style={{ border: 0 }}>
            <StyledMyMatchDynamicBackground isActive={matchViewMode === MatchViewModes.MY_MATCH} />
            {activeItemBgPosition ? (
              <ActiveWidgetBackground $position={activeItemBgPosition} $matchViewMode={matchViewMode} />
            ) : null}
            {isStreamHighlightsEnabled && hasHighlights ? (
              <div
                id={highlightsItemIdRef.current}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onClickHighlights}
              >
                <StyledHighlightsIcon />
              </div>
            ) : null}
            {hasStream ? (
              <div id={tvItemIdRef.current} className="sr-tabs-tab__wrapper srm-is-fullwidth" onClick={onClickTvItem}>
                <StyledTvIcon />
              </div>
            ) : null}
            {isEventCenterWidgetAvailable && sportId === SportIds.GOLF && isLive && !hasStream ? (
              <div
                id={tvItemIdRef.current}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onGolfWinaTvButtonClick}
              >
                <StyledTvIcon />
              </div>
            ) : null}
            {isLineUpAvailable ? (
              <LineUpButtonWrapper
                id="line-up-button"
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onLineUpButtonClick}
                data-test-tab="line-up-button"
              >
                {hasLineUpNewTag ? <StyledNewTag>New</StyledNewTag> : null}
                <span className="sr-tabs-tab__content srt-text-secondary srm-is-start">
                  <StyledLineUpIcon sportId={sportId} />
                </span>
              </LineUpButtonWrapper>
            ) : null}
            {displayLiveTable ? (
              <div
                id={liveTableIdRef.current}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onClickLiveTable}
              >
                <StyledPodium />
              </div>
            ) : null}
            {displayCupRoster ? (
              <div
                id={cupRosterIdRef.current}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onClickCupRoster}
              >
                <StyledRanking />
              </div>
            ) : null}
            {sportradarAvailable && !hiddenWidgetIds.find((widgetId) => widgetId === defaultTrackerTab) ? (
              <div
                id={defaultTrackerTab}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onClickSportradarItem}
                data-test-tab="pitch"
              >
                {defaultTrackerTab === 'track' ? (
                  <span className="sr-tabs-tab__content srt-text-secondary srm-is-start">
                    <Track className="sr-tabs-tab__icon" />
                  </span>
                ) : (
                  <span className="sr-tabs-tab__content srt-text-secondary srm-is-start">
                    <Pitch className="sr-tabs-tab__icon" />
                  </span>
                )}
              </div>
            ) : null}
            {isEventCenterWidgetAvailable ? (
              <EventCenterButtonWrapper
                id={EVENT_CENTER_WIDGET_BUTTON_ID}
                className="sr-tabs-tab__wrapper srm-is-fullwidth"
                onClick={onEventCenterButtonClick}
                data-test-tab={EVENT_CENTER_WIDGET_BUTTON_ID}
              >
                <StyledNewTag>New</StyledNewTag>
                <span className="sr-tabs-tab__content srt-text-secondary srm-is-start">{EventCenterButtonIcon}</span>
              </EventCenterButtonWrapper>
            ) : null}
          </Toolbar>
          <EventCenterNodeContext.Provider value={eventCenterNodeForwardedState}>
            <Content
              isStreamPaused={isStreamPaused}
              matchId={matchId}
              shouldUsePlaceholder={shouldUsePlaceholder}
              isStreaming={isStreaming}
              sportradarHidden={sportradarHidden}
              isTvItemSelected={isTvItemSelected}
              highlights={highlights}
              hasHighlights={hasHighlights && isStreamHighlightsEnabled}
              isHighlightsItemSelected={isHighlightsItemSelected}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              tvItemId={tvItemIdRef.current}
              trackerDomNodeRef={trackerDomNodeRef}
              hasStickyContent={stickyContent}
              disabled={disableSticky}
              zIndex={theme.ZIndices.overlay - 1}
              setContentSize={setWidgetSize}
              placeholderStyle={{ marginBottom: isWidgetOpen ? '0.5em' : '0' }}
              jwt={jwt}
              streamData={streamData}
              sportId={sportId}
              categoryId={categoryId}
              widgetWarningMessage={currentWidgetWarning?.message}
            />
          </EventCenterNodeContext.Provider>
        </>
      ) : (
        <NoToolbar />
      )}
    </Wrapper>
  );
};

export default MatchToolbar;
